import { styled, alpha, type Theme } from '@mui/material/styles';
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  Select,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import { spacing, colors } from '../../../styles/theme';

const tableHeaderBg = (theme: Theme) =>
  theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : theme.palette.grey[50];

const subtleSurface = (theme: Theme) =>
  theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : theme.palette.grey[50];

const transferFilterInputSurface = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? {
        backgroundColor: alpha(theme.palette.common.white, 0.06),
        '&:hover': {
          backgroundColor: alpha(theme.palette.common.white, 0.1),
          '& fieldset': { borderColor: theme.palette.text.secondary, borderWidth: '2px' },
        },
        '&.Mui-focused': {
          backgroundColor: alpha(theme.palette.common.white, 0.12),
          '& fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        },
      }
    : {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        '&.Mui-focused': {
          backgroundColor: '#ffffff',
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 20px rgba(59, 130, 246, 0.15)',
        },
      };

// Styled components para consistencia de estilos
export const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  backgroundColor: 'transparent',
  marginBottom: 0,
}));

export const FilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  gap: theme.spacing(2),
  width: '100%',
  alignItems: 'center',
  paddingLeft: 0,
  paddingRight: 0,
}));

export const SecondaryFilterRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr 1fr',
  gap: theme.spacing(2.5),
  width: '100%',
  alignItems: 'end',
  marginTop: theme.spacing(3),
  paddingLeft: 0,
  paddingRight: 0,
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(4, 1fr)',
  },
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backdropFilter: theme.palette.mode === 'dark' ? 'none' : 'blur(10px)',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      borderColor: theme.palette.divider,
      borderWidth: '1px',
    },
    ...transferFilterInputSurface(theme),
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    '& legend': {
      maxWidth: 'none',
      width: 'fit-content',
      minWidth: 0,
      padding: '0 8px',
      boxSizing: 'content-box',
    },
  },
}));

export const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 8px 25px ${alpha(theme.palette.common.black, 0.45)}`
        : '0 8px 25px rgba(0, 0, 0, 0.15)',
  },
}));

// Table styles
export const TableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
}));

export const Table = styled('table')(({ theme }) => ({
  minWidth: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
}));

export const TableHead = styled('thead')(({ theme }) => ({
  backgroundColor: tableHeaderBg(theme),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const TableRow = styled('tr')(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.04)
        : theme.palette.grey[50],
    transition: 'background-color 0.2s ease-in-out',
  },
}));

export const TableHeaderCell = styled('th')(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  textAlign: 'left',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: 'capitalize',
  letterSpacing: 0.8,
  borderBottom: `1px solid ${theme.palette.divider}`,
  whiteSpace: 'nowrap',
  backgroundColor: tableHeaderBg(theme),
}));

export const TableBody = styled('tbody')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  '& tr:not(:last-child)': {
    borderBottom: 'none',
  },
}));

export const TableCell = styled('td')(({ theme }) => ({
  paddingTop: theme.spacing(0.6),
  paddingBottom: theme.spacing(0.6),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  fontSize: '1rem',
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
}));

// Status chip styles
export const StatusChip = styled('span')<{ status: string }>(({ theme, status }) => ({
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  display: 'inline-flex',
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: theme.spacing(3),
  transition: 'all 0.2s ease-in-out',
  ...(status === 'Completado' && {
    backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.22 : 0.15),
    color: theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark,
  }),
  ...(status === 'Pendiente' && {
    backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.22 : 0.15),
    color: theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.dark,
  }),
  ...(status === 'Cancelado' && {
    backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.15),
    color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
  }),
}));

// Styled IconButton base
export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Styled IconButton para ver
export const ViewIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: colors.primary.main,
  '&:hover': {
    color: colors.primary.dark,
    backgroundColor: alpha(colors.primary.main, 0.12),
  },
}));

// Styled IconButton para imprimir
export const PrintIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: colors.primary.main,
  '&:hover': {
    color: colors.primary.dark,
    backgroundColor: alpha(colors.primary.main, 0.12),
  },
}));

// Styled IconButton para aceptar
export const AcceptIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: colors.primary.main,
  '&:hover': {
    color: colors.primary.dark,
    backgroundColor: alpha(colors.primary.main, 0.12),
  },
}));

// Styled IconButton para rechazar
export const RejectIconButton = styled(StyledIconButton)(({ theme }) => ({
  color: colors.error.main,
  '&:hover': {
    color: colors.error.dark,
    backgroundColor: alpha(colors.error.main, 0.12),
  },
}));

// Pagination styles
export const PaginationContainer = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

export const PaginationContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

export const InfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up('md')]: {
    marginBottom: 0,
  },
}));

export const TotalItemsText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '12px',
  '& .highlight': {
    color: theme.palette.primary.main,
  },
}));

export const RecordsText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

export const ItemsPerPageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

export const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: '12px',
  '& .MuiSelect-select': {
    paddingTop: '4px',
    paddingBottom: '4px',
  },
}));

export const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const NavigationButton = styled(IconButton)(({ theme }) => ({
  width: '24px',
  height: '24px',
  borderRadius: theme.spacing(0.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

export const PageButton = styled('button')(({ theme }) => ({
  width: '24px',
  height: '24px',
  border: `1px solid ${theme.palette.divider}`,
  fontSize: '12px',
  fontWeight: 500,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&.active': {
    background: 'linear-gradient(to right, #06b6d4, #10b981)',
    color: theme.palette.common.white,
    borderColor: 'transparent',
  },
  '&:not(.active)': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

export const EllipsisContainer = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  fontSize: '12px',
  fontWeight: 500,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Modal styles
export const ModalOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
}));

export const ModalContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  boxShadow:
    theme.palette.mode === 'dark'
      ? `0 10px 25px ${alpha(theme.palette.common.black, 0.55)}`
      : '0 10px 25px rgba(0, 0, 0, 0.2)',
  maxWidth: '1024px',
  width: '100%',
  margin: theme.spacing(2),
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

export const ModalHeader = styled('div')(({ theme }) => ({
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: theme.palette.common.white,
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const ModalTitle = styled('h2')(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  margin: 0,
}));

export const ModalCloseButton = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.palette.common.white,
  cursor: 'pointer',
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
  transition: 'color 0.2s ease-in-out',
  '&:hover': {
    color: alpha(theme.palette.common.white, 0.85),
  },
  '& svg': {
    width: '24px',
    height: '24px',
  },
}));

export const ModalContent = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto',
  maxHeight: 'calc(90vh - 120px)',
  flex: 1,
}));

export const ModalFooter = styled('div')(({ theme }) => ({
  backgroundColor: subtleSurface(theme),
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  display: 'flex',
  justifyContent: 'flex-end',
}));

// Loading states
export const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

export const LoadingSpinner = styled('div')(({ theme }) => ({
  display: 'inline-block',
  width: '32px',
  height: '32px',
  border: `2px solid ${alpha(theme.palette.divider, 0.5)}`,
  borderTop: `2px solid ${theme.palette.success.main}`,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  marginBottom: theme.spacing(1),
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

export const LoadingText = styled('p')(({ theme }) => ({
  color: theme.palette.text.secondary,
  margin: 0,
}));

// Empty state
export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: subtleSurface(theme),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
}));

export const EmptyStateIcon = styled('svg')(({ theme }) => ({
  width: '48px',
  height: '48px',
  color: theme.palette.action.disabled,
  marginBottom: theme.spacing(2),
}));

export const EmptyStateTitle = styled('p')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  margin: 0,
  marginBottom: theme.spacing(0.5),
}));

export const EmptyStateSubtitle = styled('p')(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  margin: 0,
}));

// Dialog styles
export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(1),
    maxWidth: '768px',
    width: '100%',
  },
}));

export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: theme.palette.common.white,
  borderRadius: `${spacing.lg} ${spacing.lg} 0 0`,
  fontSize: '20px',
  fontWeight: 600,
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

// Botón de cerrar
export const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  '&:hover': {
    color: alpha(theme.palette.common.white, 0.85),
    backgroundColor: alpha(theme.palette.common.white, 0.12),
  },
}));

export const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
}));

export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  backgroundColor: subtleSurface(theme),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

// Alert styles
export const StyledAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

// Chip styles
export const StyledChip = styled(Chip)(({ theme }) => ({
  fontSize: '12px',
  height: '24px',
}));

// Dashboard styles
export const DashboardContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: subtleSurface(theme),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
}));

export const DashboardCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  borderRadius: theme.spacing(1),
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

export const DashboardValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  color: theme.palette.text.primary,
}));

export const DashboardLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: 1,
}));

export const ProgressBarContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '12px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.12)
      : theme.palette.grey[200],
  borderRadius: theme.spacing(1.5),
  overflow: 'hidden',
  marginTop: theme.spacing(1),
}));

export const ProgressBar = styled(Box)<{ percentage: number }>(({ theme, percentage }) => ({
  height: '100%',
  width: `${percentage}%`,
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  transition: 'width 0.3s ease-in-out',
  borderRadius: theme.spacing(1.5),
}));

export const DraftNotification = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  animation: 'slideIn 0.3s ease-out',
  '@keyframes slideIn': {
    from: {
      transform: 'translateY(-10px)',
      opacity: 0,
    },
    to: {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
}));
